import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Paper, Typography, Button, withStyles, Select, MenuItem, InputLabel, FormControl } from '@material-ui/core';
import { DropzoneArea } from 'material-ui-dropzone'
import { connect } from "react-redux";
import * as actions from './store/uploadActions';
import { snackActions } from '../../sc-utilities/SnackBar';
import { capitalize } from '../../sc-utilities';
import AppLogoLoading from '../../sc-logo';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        paddingRight: theme.spacing(6),
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            paddingRight: 0,
        }
    },
    paper: {
        backgroundColor: "transparent",
        boxShadow: "none",
        overflow: "hidden",
        alignItems: "center"
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        marginTop: "30px"
    }
});

const UploadForm = props => {
    const { classes, translations, activeCustomerID, fetchOrder, fetchColli, uploadErasureData } = props;
    const [uploadState, updateUploadState] = useState({ filesToSend: [], uploadButtonDisabled: false, openProgressModal: false, orderID: null, transportColliID: null, colliID: null });
    const [logisticData, setLogisticData] = useState({ collies: [], orders: [], selectedOrder: null });
    const [key, setKey] = useState('dropzone_key');
    const [progress, updateProgress] = useState({ generated: 0, toGenerate: 0 });

    useEffect(() => {
        const fetchData = async () => {
            let collies = await fetchColli(activeCustomerID);
            let orders = await fetchOrder(activeCustomerID);

            setLogisticData({ collies: collies, orders: orders });
        }
        fetchData();
    }, [fetchOrder, fetchColli, activeCustomerID]);


    const handleSubmit = useCallback(async () => {
        const token = localStorage.getItem('SESSION');
        let filesToSendLength = uploadState.filesToSend.length < 1;
        if (filesToSendLength) {
            return snackActions.snackBar({ message: `No files to upload.`, options: { variant: 'warning' } });
        }
        updateProgress({ generated: 0, toGenerate: uploadState.filesToSend.length });
        if (!uploadState.orderID || !uploadState.transportColliID || !uploadState.colliID) {
            return snackActions.snackBar({ message: `Missing logistic data.`, options: { variant: 'warning' } });
        }

        const data = new FormData();
        let counter = 0;
        for (const file of uploadState.filesToSend) {
            data.append(`file_${counter}`, file);
            counter++;
        }
        data.append('ActiveCustomerID', activeCustomerID);
        data.append('TransportColliID', uploadState.transportColliID);
        data.append('OrderID', uploadState.orderID);
        data.append('ColliID', uploadState.colliID);
        data.append('Token', token);

        updateUploadState({ ...uploadState, buttonDisabled: true, openProgressModal: true });
        const res = await uploadErasureData(data, updateProgress);
        if (!res) {
            updateUploadState({ ...uploadState, buttonDisabled: false, openProgressModal: false });
            setKey(null);
        } else {
            updateUploadState({ filesToSend: [], uploadButtonDisabled: true, openProgressModal: false, transportColliID: null, colliID: null });
            setKey(null);
        }
    }, [uploadErasureData, uploadState, activeCustomerID]);

    const handleChange = useCallback(event => {
        let filesToSendLength = uploadState.filesToSend.length < 1;
        switch (event?.target?.name) {
            case 'Order':
                updateUploadState({ ...uploadState, orderID: event?.target?.value, transportColliID: "", uploadButtonDisabled: filesToSendLength === true || uploadState.colliID === null || uploadState.transportColliID });
                break;
            case 'TransportColli':
                updateUploadState({ ...uploadState, transportColliID: event?.target?.value, uploadButtonDisabled: filesToSendLength === true || uploadState.colliID === null });
                break;
            case 'Colli':
                updateUploadState({ ...uploadState, colliID: event?.target?.value, uploadButtonDisabled: filesToSendLength === true || uploadState.transportColliID === null });
                break;
            default:
                console.log('Something went wrong');
        }
    }, [uploadState]);

    return useMemo(() => {
        const handleUpload = event => {
            let files = event;
            if (!(files instanceof Array) && files.length > 0) {
                updateUploadState({ ...uploadState, filesToSend: [], uploadButtonDisabled: true, openProgressModal: false });
                return
            }

            updateUploadState({ ...uploadState, filesToSend: files, uploadButtonDisabled: uploadState.transportColliID === null || uploadState.colliID === null, openProgressModal: false });
        }
        let selectedOrder = logisticData?.orders?.find(order => order.value.toString() === uploadState?.orderID?.toString());

        if (progress.toGenerate <= progress.generated && progress.toGenerate !== 0) {
            setKey(null);
            updateUploadState({ filesToSend: [], uploadButtonDisabled: false, openProgressModal: false, orderID: null, transportColliID: null, colliID: null });
            updateProgress({ generated: 0, toGenerate: 0 });
        }

        return <>
            <Paper className={classes.root} >
                <FormControl className={classes.formControl}>
                    <InputLabel id="select_order">{translations["select_order"] || "Select order"}</InputLabel>
                    <Select
                        labelId="select_order"
                        id="select_order"
                        name="Order"
                        label={translations['order'] || 'Order'}
                        margin={"dense"}
                        placeholder={translations["select_order"] || "Select order"}
                        value={uploadState?.orderID || ""}
                        onChange={handleChange}
                        fullWidth={true}
                    >
                        {(logisticData?.orders || []).map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.displayValue}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.formControl}>
                    <InputLabel id="select_transport_colli">{translations["select_transport_colli"] || "Select transport colli"}</InputLabel>
                    <Select
                        labelId="select_transport_colli"
                        id="select_transport_colli"
                        name="TransportColli"
                        label={translations['transport_colli'] || 'Transport colli'}
                        margin={"dense"}
                        placeholder={translations["select_transport_colli"] || "Select transport colli"}
                        value={uploadState?.transportColliID || ""}
                        onChange={handleChange}
                        fullWidth={true}
                    >
                        {(selectedOrder?.TransportCollies || []).map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.displayValue}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.formControl}>
                    <InputLabel id="select_colli">{translations["select_colli"] || "Select colli"}</InputLabel>
                    <Select
                        labelId="select_colli"
                        id="select_colli"
                        name="Colli"
                        label={translations['colli'] || 'Colli'}
                        margin={"dense"}
                        value={uploadState?.colliID || ""}
                        onChange={handleChange}
                        fullWidth={true}
                    >
                        {(logisticData?.collies || []).map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.displayValue}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography>
                    {translations['supported_format'] || 'Supported format:'}{' .json'}.<br />
                </Typography>
                <DropzoneArea
                    filesLimit={1000}
                    key={key}
                    dropzoneText={`${translations['add_erasure_data_upload_box'] || 'Drag and drop an .json file or click here to add'}`}
                    onChange={handleUpload}
                    acceptedFiles={['.json']}
                    getFileRemovedMessage={(fileName) => {
                        const text = translations ? (
                            capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['removed'] || 'removed'}.`)
                        ) : `File ${fileName} removed.`

                        return text;
                    }}
                    getFileAddedMessage={(fileName) => {
                        const text = translations ? (
                            capitalize(`${translations['file'] || 'File'} ${fileName} ${translations['successfully_added'] || 'successfully added'}.`)
                        ) : `File ${fileName} successfully added.`

                        return text;
                    }}
                    getFileLimitExceedMessage={(filesLimit) => {
                        const text = translations ? (
                            capitalize(`${translations['maximum_allowed_number'] || 'Maximum allowed number of files exceeded. Only'} ${filesLimit} ${translations['allowed'] || 'allowed'}.`)
                        ) : `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`

                        return text;
                    }}
                    getDropRejectMessage={(rejectedFile) => {
                        const text = translations ? (
                            capitalize(`${translations['File'] || 'File'} ${translations['was_rejected'] || 'was rejected'}.`)
                        ) : `File was rejected.`

                        return text;
                    }}
                />
                <Button
                    className={classes.submit}
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit}
                    fullWidth={true}
                    disabled={uploadState.uploadButtonDisabled}
                >
                    {translations['import'] || "Import"}
                </Button>
            </Paper>

            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={true}
                open={uploadState.openProgressModal}
                PaperProps={{
                    classes: {
                        root: classes.paper
                    }
                }}
            >
                <AppLogoLoading />
                <br />
                <h1 style={{ color: 'white' }}>{translations['importing'] || 'Importing'}: {progress.generated + 1}/{progress.toGenerate}</h1>
            </Dialog>
        </>
    }, [logisticData, classes, handleSubmit, translations, uploadState, handleChange, key, progress.generated, progress.toGenerate]);
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state?.translate?.translations?.activeTranslations ?? {}
    };
};

const mapDispatchToProps = dispatch => {
    return {
        uploadErasureData: (data, updateProgress) => dispatch(actions.uploadErasureData(data, updateProgress)),
        fetchOrder: activeCustomerID => dispatch(actions.fetchOrder(activeCustomerID)),
        fetchColli: activeCustomerID => dispatch(actions.fetchColli(activeCustomerID))

    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(UploadForm));